class SkFaq extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.buildFaqContent();
    this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
    this.allowMultiple = this.dataset.allowMultiple === 'true';
    this.classList.add('is-enhanced');

    const initiallyOpen = this.items.filter((item) => item.classList.contains('is-open'));
    if (!this.allowMultiple && initiallyOpen.length > 1) {
      initiallyOpen.slice(1).forEach((item) => this.setItem(item, false));
    }

    this.items.forEach((item) => {
      this.setItem(item, item.classList.contains('is-open'));
      item.querySelector('[data-faq-trigger]')?.addEventListener('click', () => this.toggleItem(item));
    });

    this.addEventListener('shopify:block:select', (event) => {
      const item = event.target.closest('[data-faq-item]');
      if (item) this.openItem(item);
    });

    this.setupReveal();
  }

  buildFaqContent() {
    const list = this.querySelector('[data-faq-list]');
    const itemTemplate = this.querySelector('template[data-faq-item-template]');
    const definitionsTemplate = this.querySelector('template[data-faq-definitions]');
    if (!list || !itemTemplate || !definitionsTemplate) return;

    const definitions = Array.from(definitionsTemplate.content.querySelectorAll('[data-faq-definition]')).map(
      (definition) => ({
        element: definition,
        blockId: definition.dataset.blockId || '',
        manualOverride: definition.dataset.manualOverride === 'true',
        open: definition.dataset.openByDefault === 'true',
        question: definition.querySelector('[data-manual-question]')?.textContent?.trim() || '',
        answer: definition.querySelector('[data-manual-answer]')?.innerHTML?.trim() || '',
      })
    );

    const source = this.dataset.faqSource || 'product';
    const productFaqs = source === 'product' ? this.parseProductFaqs() : [];
    let entries = [];

    if (productFaqs.length > 0) {
      const lastManualIndex = definitions.reduce(
        (lastIndex, definition, index) => (definition.manualOverride ? index : lastIndex),
        -1
      );
      const total = Math.max(productFaqs.length, lastManualIndex + 1);

      for (let index = 0; index < total; index += 1) {
        const definition = definitions[index];
        const productFaq = productFaqs[index];

        if (definition?.manualOverride) {
          if (definition.question && definition.answer) {
            entries.push({ ...definition, source: 'manual' });
          }
        } else if (productFaq) {
          entries.push({
            ...productFaq,
            source: 'product',
            blockId: definition?.blockId || '',
            element: definition?.element || null,
            open: definition ? definition.open : index === 0 && this.dataset.openFirstProductFaq === 'true',
          });
        }
      }
    } else if (source === 'manual' || this.dataset.noProductFaqFallback === 'manual') {
      entries = definitions
        .filter((definition) => definition.question && definition.answer)
        .map((definition) => ({ ...definition, source: 'manual' }));
    }

    list.replaceChildren();
    entries.forEach((entry, index) => {
      const fragment = itemTemplate.content.cloneNode(true);
      const item = fragment.querySelector('[data-faq-item]');
      const trigger = fragment.querySelector('[data-faq-trigger]');
      const panel = fragment.querySelector('[data-faq-panel]');
      const question = fragment.querySelector('[data-faq-question]');
      const answer = fragment.querySelector('[data-faq-answer]');
      if (!item || !trigger || !panel || !question || !answer) return;

      const itemId = entry.blockId || `${entry.source}-${index + 1}`;
      const panelId = `SkfaqPanel-${this.dataset.sectionId}-${itemId}`;
      item.style.setProperty('--skfaq-index', String(index));
      item.dataset.faqSource = entry.source;
      item.classList.toggle('is-open', Boolean(entry.open));
      trigger.setAttribute('aria-controls', panelId);
      panel.id = panelId;
      question.textContent = entry.question;
      answer.innerHTML = entry.answer;

      if (entry.element) {
        Array.from(entry.element.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify-')) item.setAttribute(attribute.name, attribute.value);
        });
      }

      list.appendChild(fragment);
    });

    if (entries.length === 0) list.hidden = true;
  }

  parseProductFaqs() {
    const descriptionTemplate = this.querySelector('template[data-product-description]');
    if (!descriptionTemplate) return [];

    const container = document.createElement('div');
    container.appendChild(descriptionTemplate.content.cloneNode(true));

    const marker = (this.dataset.faqHeadingMarker || 'FAQ').trim().toLocaleLowerCase();
    const headings = Array.from(container.querySelectorAll('h3'));
    const faqHeading = headings.find((heading) => heading.textContent.trim().toLocaleLowerCase() === marker);
    if (!faqHeading) return [];

    const contentElements = Array.from(container.querySelectorAll('h3, h4, p, ul, ol, blockquote, table'));
    const startIndex = contentElements.indexOf(faqHeading);
    const maxItems = Math.max(1, Number.parseInt(this.dataset.maxProductFaqs || '12', 10));
    const faqs = [];
    let current = null;

    for (const element of contentElements.slice(startIndex + 1)) {
      if (element.tagName === 'H3') break;

      if (element.tagName === 'H4') {
        if (current?.question && current.answerParts.length > 0) {
          faqs.push({ question: current.question, answer: current.answerParts.join(''), open: false });
          if (faqs.length >= maxItems) return faqs;
        }

        current = { question: element.textContent.trim(), answerParts: [] };
        continue;
      }

      if (!current || !element.textContent.trim()) continue;
      if (element.parentElement?.closest('ul, ol, blockquote, table')) continue;
      current.answerParts.push(element.outerHTML);
    }

    if (current?.question && current.answerParts.length > 0 && faqs.length < maxItems) {
      faqs.push({ question: current.question, answer: current.answerParts.join(''), open: false });
    }

    return faqs;
  }

  toggleItem(item) {
    const willOpen = !item.classList.contains('is-open');
    const trigger = item.querySelector('[data-faq-trigger]');

    if (willOpen && !this.allowMultiple) {
      this.items.forEach((otherItem) => {
        if (otherItem !== item) this.setItem(otherItem, false);
      });
    }

    this.setItem(item, willOpen);
    trigger?.classList.remove('is-clicked');
    requestAnimationFrame(() => trigger?.classList.add('is-clicked'));
    window.setTimeout(() => trigger?.classList.remove('is-clicked'), 460);
  }

  openItem(item) {
    if (!this.allowMultiple) {
      this.items.forEach((otherItem) => {
        if (otherItem !== item) this.setItem(otherItem, false);
      });
    }
    this.setItem(item, true);
  }

  setItem(item, open) {
    const trigger = item.querySelector('[data-faq-trigger]');
    const panel = item.querySelector('[data-faq-panel]');

    item.classList.toggle('is-open', open);
    trigger?.setAttribute('aria-expanded', String(open));
    panel?.setAttribute('aria-hidden', String(!open));
    if (panel) panel.inert = !open;
  }

  setupReveal() {
    const targets = this.querySelectorAll('.skfaq-reveal');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.dataset.animate !== 'true' || reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    targets.forEach((target) => observer.observe(target));
  }
}

if (!customElements.get('sk-faq')) {
  customElements.define('sk-faq', SkFaq);
}
